import { cardLabel } from "../cards/card-art";
import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import type { PlayerProjection } from "@saboteur/contracts";
import { CardFace } from "../cards/card-face";
type HandCard = PlayerProjection["self"]["hand"][number];
export interface PlayerHandProps {
  cards: readonly HandCard[];
  selectedCardId?: string | undefined;
  selectedRotation?: 0 | 180;
  playableCardIds: readonly string[];
  disabled?: boolean;
  onSelect: (card: HandCard) => void;
}
export function PlayerHand({
  cards,
  selectedCardId,
  selectedRotation = 0,
  playableCardIds,
  disabled,
  onSelect,
}: PlayerHandProps) {
  const [initialCards] = useState(() => new Set(cards.map((card) => card.cardId)));
  const list = useRef<HTMLUListElement>(null);
  const previous = useRef(new Map<string, number>());
  const cardIds = cards.map((card) => card.cardId).join(",");
  useLayoutEffect(() => {
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!reducedMotion) return;
    const animations: Animation[] = [];
    const positions = new Map<string, number>();
    list.current?.querySelectorAll<HTMLLIElement>("li[data-hand-id]").forEach((item) => {
      const id = item.dataset.handId!;
      const left = item.offsetLeft;
      const oldLeft = previous.current.get(id);
      positions.set(id, left);
      if (reducedMotion.matches || !item.animate) return;
      if (oldLeft !== undefined && oldLeft !== left) {
        animations.push(
          item.animate([{ transform: `translateX(${oldLeft - left}px)` }, { transform: "none" }], {
            duration: 260,
            easing: "cubic-bezier(.2,.75,.25,1)",
          }),
        );
      }
    });
    previous.current = positions;
    const cancel = () => animations.forEach((animation) => animation.cancel());
    reducedMotion.addEventListener("change", cancel);
    return () => {
      cancel();
      reducedMotion.removeEventListener("change", cancel);
    };
  }, [cardIds]);
  return (
    <section className="player-hand" aria-label="Your hand">
      <ul className="player-hand__cards" ref={list}>
        {cards.map((card, index) => (
          <li
            key={card.cardId}
            data-hand-id={card.cardId}
            style={
              {
                "--deal-delay": `${initialCards.has(card.cardId) ? index * 45 : 0}ms`,
              } as CSSProperties
            }
          >
            <button
              className="hand-card"
              data-card-id={card.cardId}
              data-selected={selectedCardId === card.cardId}
              data-legal={playableCardIds.includes(card.cardId)}
              disabled={disabled}
              onClick={() => onSelect(card)}
              aria-pressed={selectedCardId === card.cardId}
              aria-label={cardLabel(card.faceId)}
              type="button"
            >
              <CardFace
                faceId={card.faceId}
                rotation={
                  selectedCardId === card.cardId && card.kind === "PATH" ? selectedRotation : 0
                }
              />
              <span>{cardLabel(card.faceId)}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
